import React, { useEffect, useState } from 'react';
import { Button, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import filter from 'lodash/filter';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import {
  getActiveStep,
  getItemDescription,
  getItemPrice,
  getItemTitle,
  getSteps,
  getStepsState,
  getVisibleComponents,
  stepHasSelectableComponents,
} from '../../Reducers/Configurator/Selectors';
import { black, green, darkgrey } from '../../Constants/Colors';
import { setStepState, setInitialActiveStep } from '../../Reducers/Configurator/Actions';
import AdditionalInfoDialog from '../AdditionalInfoDialog/AdditionalInfoDialog';
import { useMediaQuery, useTheme } from '@material-ui/core';

const useStyles = makeStyles(
  (theme) => ({
    stepsContainer: {
      [theme.breakpoints.down('sm')]: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        overflowX: 'auto',
        whiteSpace: 'nowrap',
        scrollbarWidth: 'none', 
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      },
    },
    titleInfoContainer: {
      display: 'flex',
      alignItems: 'flex-end',
      gap: 12,
      marginBottom: 40,
    },
    title: {
      fontFamily: 'Inter',
    },
    step: {
      display: 'flex',
      [theme.breakpoints.down('sm')]: {
        flex: '0 0 auto',
        height: 50,
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 8,
      },
    },
    connector: {
      height: 30,
      width: 1,
      backgroundColor: darkgrey,
      marginLeft: 19,
    },
    stepTitle: { fontSize: 18, display: 'flex', alignItems: 'center', color: darkgrey },
    status: {
      borderRadius: '50%',
      border: '2px solid grey',
      height: 40,
      width: 40,
      marginRight: 10,
    },
    activeStep: {
      borderColor: green,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      '& .isActive': {
        backgroundColor: green,
        height: 30,
        width: 30,
        borderRadius: '50%',
      },
    },
    activeTitle: {
      color: black,
      [theme.breakpoints.down('sm')]: {
        textDecoration: 'underline',
        textDecorationColor: green,
      },
    },
    enabledStep: {
      borderColor: black,
    },
    btnAdditionalInfo: {
      textTransform: 'none',
      padding: 0,
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    buttonLabel: {
      fontFamily: 'Inter',
      fontWeight: 400,
      color: green,
      textDecoration: 'underline',
    },
  }),
  {
    name: 'Steps',
  }
);

const Steps = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const steps = useSelector(getSteps);
  const itemTitle = useSelector(getItemTitle);
  const itemDescription = useSelector(getItemDescription);
  const itemPrice = useSelector(getItemPrice);
  const stepsState = useSelector(getStepsState);
  const activeStep = useSelector(getActiveStep);
  const components = useSelector(getVisibleComponents);
  const enabledSteps = filter(stepsState, (step) => {
    if (step.state === 'enabled') {
      return step.identifier;
    }
  });
  const activeStepIndex = findIndex(steps, (step) => step.identifier === activeStep);

  const [isAdditionalInfoPopupOpen, setIsAdditionalInfoPopupOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (steps.length > 0 && !activeStep) {
      dispatch(setInitialActiveStep());
    }
  }, [dispatch, steps, activeStep]);

  return (
    <div className={classes.stepsContainer}>
      {!isMobile && (
        <div className={classes.titleInfoContainer}>
          <Typography variant="h2" color="inherit" className={classes.title}>
            {itemTitle}
          </Typography>
          <Button
            className={classes.btnAdditionalInfo}
            onClick={() => setIsAdditionalInfoPopupOpen(true)}
            disableRipple
          >
            <Typography variant="body1" className={classes.buttonLabel}>
              {t('steps.additionalInfo')}
            </Typography>
          </Button>
        </div>
      )}
      {steps.map((step, i) => (
        <React.Fragment key={step.identifier}>
          <div className={classes.step}>
            {!isMobile && (
              <div
                className={clsx(
                  classes.status,
                  activeStep === step.identifier && classes.activeStep,
                  find(enabledSteps, (s) => s.identifier === step.identifier) && classes.enabledStep
                )}
                style={{
                  cursor: activeStepIndex > i && stepHasSelectableComponents(step, components) ? 'pointer' : 'auto',
                }}
                onClick={() => {
                  activeStepIndex > i &&
                    stepHasSelectableComponents(step, components) &&
                    dispatch(setStepState(step.identifier, 'active'));
                }}
              >
                <div className="isActive"></div>
              </div>
            )}
            <Typography
              variant="body1"
              color="inherit"
              className={clsx(
                classes.stepTitle,
                (find(enabledSteps, (s) => s.identifier === step.identifier) ||
                  activeStep === step.identifier) &&
                  classes.activeTitle
              )}
            >
              {step.title}
            </Typography>
          </div>
          {!isMobile && i < steps.length - 1 && <div className={classes.connector}></div>}
        </React.Fragment>
      ))}
      <AdditionalInfoDialog
        title={itemTitle}
        isOpen={isAdditionalInfoPopupOpen}
        itemDescription={itemDescription}
        itemPrice={itemPrice}
        onCancel={() => setIsAdditionalInfoPopupOpen(false)}
        onButtonClick={() => setIsAdditionalInfoPopupOpen(false)}
      />
    </div>
  );
};

export default Steps;
